/**
 * Agent Workspace Tabs Context
 * Zendesk-style multi-ticket tabs - persist in sessionStorage
 */

import React, { createContext, useContext, useCallback, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const STORAGE_KEY = 'agent-workspace-tabs';
const MAX_TABS = 10;

export type TabPriority = 'low' | 'medium' | 'high' | 'urgent';

export interface WorkspaceTab {
  id: string;           // "number/12345" or uuid
  label: string;        // "#12345" or subject (truncated)
  priority?: TabPriority;
}

interface AgentWorkspaceTabsContextValue {
  tabs: WorkspaceTab[];
  activeTabId: string | null;
  addTab: (id: string, label: string, priority?: TabPriority) => void;
  replaceTabs: (id: string, label: string) => void;  // Replace all tabs with just this one (normal click)
  removeTab: (id: string) => void;
  switchToTab: (id: string) => void;
  getWorkspacePath: (tabId: string) => string;
}

const AgentWorkspaceTabsContext = createContext<AgentWorkspaceTabsContextValue | null>(null);

function loadTabsFromStorage(): WorkspaceTab[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveTabsToStorage(tabs: WorkspaceTab[]) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tabs.slice(-MAX_TABS)));
  } catch (_) {}
}

export function getWorkspacePathFromTabId(tabId: string): string {
  if (tabId.startsWith('number/')) {
    const num = tabId.replace('number/', '');
    return `/workspace/number/${num}`;
  }
  return `/workspace/${tabId}`;
}

export function getTabIdFromPath(pathname: string): string | null {
  const match = pathname.match(/\/workspace\/number\/([^/]+)/);
  if (match) return `number/${match[1]}`;
  const uuidMatch = pathname.match(/\/workspace\/([^/]+)/);
  if (uuidMatch && uuidMatch[1] !== 'new') return uuidMatch[1];
  return null;
}

export const AgentWorkspaceTabsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tabs, setTabs] = useState<WorkspaceTab[]>(loadTabsFromStorage);

  const pathTabId = getTabIdFromPath(location.pathname);
  const activeTabId = pathTabId;

  useEffect(() => {
    saveTabsToStorage(tabs);
  }, [tabs]);

  const addTab = useCallback((id: string, label: string, priority?: TabPriority) => {
    setTabs((prev) => {
      const existingIndex = prev.findIndex((t) => t.id === id);

      // Keep existing tab position stable (Chrome-like behavior):
      // opening/viewing an already-open ticket updates metadata in place.
      if (existingIndex !== -1) {
        const current = prev[existingIndex];
        if (current.label === label && current.priority === priority) return prev;

        const updated = [...prev];
        updated[existingIndex] = { ...current, label, priority };
        return updated;
      }

      // New tickets are appended to the end, with max tab cap.
      return [...prev, { id, label, priority }].slice(-MAX_TABS);
    });
  }, []);

  const replaceTabs = useCallback((id: string, label: string) => {
    setTabs([{ id, label }]);
  }, []);

  const removeTab = useCallback((id: string) => {
    setTabs((prev) => {
      const updated = prev.filter((t) => t.id !== id);
      return updated;
    });
  }, []);

  const switchToTab = useCallback((id: string) => {
    const path = getWorkspacePathFromTabId(id);
    navigate(path);
  }, [navigate]);

  const getWorkspacePath = useCallback((tabId: string) => {
    return getWorkspacePathFromTabId(tabId);
  }, []);

  const value: AgentWorkspaceTabsContextValue = {
    tabs,
    activeTabId,
    addTab,
    replaceTabs,
    removeTab,
    switchToTab,
    getWorkspacePath,
  };

  return (
    <AgentWorkspaceTabsContext.Provider value={value}>
      {children}
    </AgentWorkspaceTabsContext.Provider>
  );
};

export function useAgentWorkspaceTabs() {
  const ctx = useContext(AgentWorkspaceTabsContext);
  return ctx;
}
